/**
 * The pure rules behind the "Retire orphaned task" control: who may see
 * it, what a reason must be, and what each refusal means for the next
 * step. No React, so they are unit-testable on their own and the
 * component file exports only a component (react-refresh), matching
 * `Diagnose/log-presets.ts`.
 */
import { SupportApiError, type OrphanTaskEvidence } from '../../api/supportClient';

/** The one role that may retire. `admin.py::require_platform_admin`,
 *  edge-authz's `_ADMIN_ROLES`, and the `task.retire` capability (held by
 *  platform_admin alone, pinned in `test_task_retire_endpoint.py`) all
 *  say the same. Adding a role here changes nothing server-side except
 *  that someone now gets a button that 403s. */
export const RETIRE_TASK_ROLE = 'nexsoss:platform_admin';

/** `RetireTaskRequest.reason`'s `max_length`. */
export const RETIRE_REASON_MAX = 500;

/** The catalogue id of the runbook this control belongs to. */
export const ORPHANED_TASK_RUNBOOK_ID = 'orphaned-task-retire';

/** `admin.py::_TASK_ID_RE`. Checked client-side only so a typo is named
 *  before a round trip; the server refuses the same ids regardless. */
export const TASK_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

export function canRetireTasks(roles: readonly string[]): boolean {
  return roles.includes(RETIRE_TASK_ROLE);
}

/** The reason's length as the SERVER counts it. Pydantic's `max_length`
 *  counts code points; `String.length` counts UTF-16 units, so an emoji
 *  or an astral CJK character is 2 here and 1 there, and a 300-emoji
 *  reason the server accepts would be refused with a red counter. */
export function retireReasonLength(reason: string): number {
  return [...reason].length;
}

/** Non-empty after trimming, and within the server's bound. The reason is
 *  still SENT exactly as typed -- trimming is only the test. A reason of
 *  spaces satisfies the server's `min_length=1` and captures nothing. */
export function isRetireReasonValid(reason: string): boolean {
  return reason.trim().length > 0 && retireReasonLength(reason) <= RETIRE_REASON_MAX;
}

export function formatAge(ms: number): string {
  if (ms < 0) return '0 s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

export function errorText(err: unknown): string {
  if (err instanceof SupportApiError) {
    return `HTTP ${err.status}${err.code ? ` ${err.code}` : ''}: ${err.message}`;
  }
  return String(err);
}

/** Each refusal says what it means for the NEXT step, because the runbook
 *  records the misreadings: a 422 taken as "the task failed the checks",
 *  and any failure taken as a reason to restart the fleet adapter. */
export function describeRetireError(err: unknown): string {
  if (!(err instanceof SupportApiError)) return `Retire failed: ${String(err)}`;
  const head = errorText(err);
  switch (err.status) {
    case 409:
      if (err.code === 'task_already_terminal') {
        return `Refused (${head}). This task has already ended, and a finished task's record is not rewritten. Nothing was changed.`;
      }
      return `Refused (${head}). Nothing was changed.`;
    case 422:
      return `Refused as malformed (${head}). This is about the request — almost always the reason — not about whether the task is orphaned. Fix it and send it again.`;
    case 404:
      if (err.code === 'task_not_projected') {
        // The retire reads the key with the plain `get_latest`, which
        // answers "nothing" for a wedged storage manager as well as for
        // a wrong id. So this names both, and the wrong reflex.
        return `Refused (${head}). Nothing was changed. The bus returned no record for this id: either the id is wrong, or the platform storage manager is not answering — a wedged one returns nothing too. Do not restart the fleet adapter.`;
      }
      return `Refused (${head}). Nothing was changed.`;
    case 403:
      return `Refused (${head}). Your token does not carry platform_admin.`;
    case 503:
      return `Not retired (${head}). Nothing was touched. Retry once the platform bus is reachable — do not restart the fleet adapter, which is what creates orphans.`;
    default:
      return `Retire failed (${head}).`;
  }
}

/**
 * Why the controls are unavailable, or `undefined` when they are not.
 *
 * ONE FUNCTION, so the disabled state and the sentence beside it cannot
 * disagree -- a disabled button with no reason, or a reason beside an enabled
 * button, is how an engineer learns to stop reading either.
 */
export function endHeldBlockedReason(ev: OrphanTaskEvidence | undefined): string | undefined {
  if (!ev) return 'Check the evidence first.';
  if (ev.robot_lift) {
    return (
      `${ev.assigned_robot ?? 'The robot'} is in or boarding lift ${ev.robot_lift}` +
      `${ev.robot_lift_basis ? ` (${ev.robot_lift_basis})` : ''}. Do NOT cancel in the car: ` +
      'hold the door, immobilise the robot, push it at least 1.2 m onto the landing, then ' +
      `press Recover for ${ev.robot_lift}. A task cancelled in a car leaves the robot parked ` +
      'inside holding the lift.'
    );
  }
  if (ev.held === 'held') return undefined;
  if (ev.held === 'not_held') {
    return (
      'No robot is executing this task — the record is stale but nothing is running. ' +
      'Retire is the verb for that.'
    );
  }
  const age = ev.robot_state_age_ms;
  return (
    'Cannot tell whether a robot is still executing this task' +
    (typeof age === 'number' ? `: its robot state is ${Math.round(age / 1000)} s old` : '') +
    '. Nothing is sent on a stale cache — this verb moves a robot.'
  );
}
